import React, { createContext, useContext, useState, useCallback } from "react";
import type { AppRole } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface TargetUser {
  id: string;
  email: string;
  fullName: string;
  roles: AppRole[];
}

interface SimulatedStudent {
  studentId: string | null;
  userId?: string | null;
  preRegistrationId: string | null;
  formIvIndex: string;
  fullName: string;
  studentClass: string | null;
  semester: string | null;
  source: "students" | "pre_registration";
}

interface ImpersonationState {
  active: boolean;
  mode: "role_preview" | "user_impersonation" | "student_simulation" | null;
  previewRoles: AppRole[] | null;
  targetUser: TargetUser | null;
  simulatedStudent: SimulatedStudent | null;
  startRolePreview: (role: AppRole) => void;
  startUserImpersonation: (userId: string) => Promise<void>;
  startStudentSimulation: (formIvIndex: string) => Promise<string | null>;
  stop: () => void;
}

const defaultState: ImpersonationState = {
  active: false,
  mode: null,
  previewRoles: null,
  targetUser: null,
  simulatedStudent: null,
  startRolePreview: () => {},
  startUserImpersonation: async () => {},
  startStudentSimulation: async () => null,
  stop: () => {},
};

export const ImpersonationContext = createContext<ImpersonationState>(defaultState);

export const ImpersonationProvider: React.FC<{
  isSuperadmin: boolean;
  children: React.ReactNode;
}> = ({ isSuperadmin, children }) => {
  const [mode, setMode] = useState<"role_preview" | "user_impersonation" | "student_simulation" | null>(null);
  const [previewRoles, setPreviewRoles] = useState<AppRole[] | null>(null);
  const [targetUser, setTargetUser] = useState<TargetUser | null>(null);
  const [simulatedStudent, setSimulatedStudent] = useState<SimulatedStudent | null>(null);

  const startRolePreview = useCallback(
    (role: AppRole) => {
      if (!isSuperadmin) return;
      setMode("role_preview");
      setPreviewRoles([role]);
      setTargetUser(null);
      setSimulatedStudent(null);
    },
    [isSuperadmin]
  );

  const startUserImpersonation = useCallback(
    async (userId: string) => {
      if (!isSuperadmin) return;

      const [profileRes, rolesRes] = await Promise.all([
        supabase.from("profiles").select("full_name").eq("user_id", userId).single(),
        supabase.rpc("get_user_roles", { _user_id: userId }),
      ]);

      const fullName = profileRes.data?.full_name || "Unknown User";
      const roles = (rolesRes.data as AppRole[]) || [];

      setMode("user_impersonation");
      setPreviewRoles(roles);
      setTargetUser({ id: userId, email: "", fullName, roles });
      setSimulatedStudent(null);
    },
    [isSuperadmin]
  );

  const startStudentSimulation = useCallback(
    async (formIvIndex: string): Promise<string | null> => {
      if (!isSuperadmin) return "Not authorized";

      // 1. Try students table
      const { data: studentRow } = await supabase
        .from("students")
        .select("id, user_id, first_name, last_name, class, form_iv_index")
        .eq("form_iv_index", formIvIndex)
        .maybeSingle();

      if (studentRow) {
        let roles: AppRole[] = ["student"];
        if (studentRow.user_id) {
          const { data: roleData } = await supabase.rpc("get_user_roles", { _user_id: studentRow.user_id });
          const assignedRoles = ((roleData as AppRole[] | null) ?? []).filter(Boolean);
          roles = assignedRoles.length > 0 ? assignedRoles : ["student"];
          if (!roles.includes("student")) roles = ["student", ...roles];
        }

        setMode("student_simulation");
        setPreviewRoles(roles);
        setTargetUser(null);
        setSimulatedStudent({
          studentId: studentRow.id,
          userId: studentRow.user_id,
          preRegistrationId: null,
          formIvIndex: studentRow.form_iv_index || formIvIndex,
          fullName: `${studentRow.first_name} ${studentRow.last_name}`,
          studentClass: studentRow.class,
          semester: null,
          source: "students",
        });
        return null;
      }

      // 2. Fallback to pre-registrations
      const { data: preReg } = await supabase
        .from("student_pre_registrations")
        .select("id, full_name, class, semester, form_iv_index")
        .eq("form_iv_index", formIvIndex)
        .maybeSingle();

      if (preReg) {
        setMode("student_simulation");
        setPreviewRoles(["student"]);
        setTargetUser(null);
        setSimulatedStudent({
          studentId: null,
          preRegistrationId: preReg.id,
          formIvIndex: preReg.form_iv_index,
          fullName: preReg.full_name || "Pre-registered Student",
          studentClass: preReg.class,
          semester: preReg.semester,
          source: "pre_registration",
        });
        return null;
      }

      return "No student or pre-registration found with that Form IV index number.";
    },
    [isSuperadmin]
  );

  const stop = useCallback(() => {
    setMode(null);
    setPreviewRoles(null);
    setTargetUser(null);
    setSimulatedStudent(null);
  }, []);

  const active = mode !== null;

  return (
    <ImpersonationContext.Provider
      value={{ active, mode, previewRoles, targetUser, simulatedStudent, startRolePreview, startUserImpersonation, startStudentSimulation, stop }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => {
  return useContext(ImpersonationContext);
};
